import React, { useState } from 'react';
import { Button, Upload, message, Card, Typography, Space, Divider, Input } from 'antd';
import { UploadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { uploadExcelForContactExtraction } from '../apis/admin';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;

/** 붙여넣은 JSON에서 profile.name, influencerInfos.name 추출용 타입 */
interface InfluencerJsonItem {
  id?: number;
  profile?: { name?: string | null };
  influencerInfos?: { name?: string | null } | null;
  blogInfos?: { url?: string | null } | null;
  representativeChannelInfos?: { url?: string | null } | null;
  [key: string]: unknown;
}

const Utility: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  const handleFileUpload = async (file: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadExcelForContactExtraction(formData);

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customer-contacts-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('고객 연락처가 성공적으로 추출되었습니다.');
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error('파일 업로드 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    name: 'file',
    accept: '.xlsx,.xls',
    beforeUpload: (file: File) => {
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls');

      if (!isExcel) {
        message.error('엑셀 파일만 업로드 가능합니다.');
        return false;
      }

      handleFileUpload(file);
      return false; // Prevent default upload behavior
    },
    showUploadList: false,
  };

  const handleExportInfluencerNames = () => {
    if (!jsonInput.trim()) {
      message.warning('JSON 데이터를 붙여넣어 주세요.');
      return;
    }
    setExportLoading(true);
    try {
      const parsed = JSON.parse(jsonInput.trim()) as { data?: InfluencerJsonItem[] } | InfluencerJsonItem[];
      const list: InfluencerJsonItem[] = Array.isArray(parsed) ? parsed : parsed?.data ?? [];
      if (!list.length) {
        message.warning('추출할 수 있는 항목이 없습니다. data 배열이 있는 JSON을 붙여넣어 주세요.');
        setExportLoading(false);
        return;
      }
      const rows = list.map((item) => ({
        유저이름: item.profile?.name ?? '',
        인플루언서명: item.influencerInfos?.name ?? '',
        블로그주소: item.blogInfos?.url ?? '',
        대표채널: item.representativeChannelInfos?.url ?? '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '이름목록');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = `brand-connector-creator-names-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      saveAs(blob, fileName);
      message.success(`엑셀 파일이 다운로드되었습니다. (${rows.length}건)`);
    } catch (e) {
      message.error('JSON 형식이 올바르지 않습니다. 확인 후 다시 시도해 주세요.');
      console.error(e);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>유틸리티</Title>

      <Card title="고객 연락처 추출" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>사용 방법:</Text>
            <ol style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>주문번호가 포함된 엑셀 파일을 준비하세요(타이틀 포함)</li>
              <li>아래 버튼을 클릭하여 파일을 업로드하세요</li>
              <li>주문번호를 기반으로 고객 이름과 연락처를 추출합니다</li>
              <li>연락처가 추가된 엑셀 파일이 자동으로 다운로드됩니다</li>
            </ol>
          </div>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Upload {...uploadProps}>
              <Button type="primary" icon={<UploadOutlined />} size="large" loading={loading}>
                엑셀 파일 업로드
              </Button>
            </Upload>
          </div>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">지원 형식: .xlsx, .xls</Text>
          </div>
        </Space>
      </Card>

      <Card title="브랜드커넥트 명단 엑셀 추출" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>사용 방법:</Text>
            <ol style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>유저 목록 JSON 데이터를 아래 입력란에 붙여넣으세요 (data 배열 또는 배열 형태)</li>
              <li>유저이름, 인플루언서명, 블로그주소, 대표채널주소 추출.</li>
            </ol>
          </div>
          <Divider />
          <TextArea
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder='예: { "data": [ { "profile": { "name": "..." }, "influencerInfos": { "name": "..." } }, ... ] } 또는 [ ... ]'
            rows={10}
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
          />
          <div style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              size="large"
              loading={exportLoading}
              onClick={handleExportInfluencerNames}>
              엑셀 추출
            </Button>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Utility;
